import React from "react";
import { Handshake, Clock, DoorOpen, AlertTriangle } from "lucide-react";
import { Hero, Section, Sub, Callout, NumberedItem, Bold } from "./_shared";
import { useT } from "@/i18n";

export default function NegotiationPage() {
  const { lang } = useT();
  return lang === "en" ? <NegotiationEN /> : <NegotiationFR />;
}

// =============================================================
// FR
// =============================================================
function NegotiationFR() {
  return (
    <div data-testid="view-negotiation" className="max-w-5xl">
      <Hero
        moduleNum="01"
        eyebrowLabel="Module"
        icon={Handshake}
        italic="Négociation"
        accent="internationale"
        description="La négociation est le cœur du métier d'intermédiaire. Sans elle, vous ne pouvez pas sécuriser vos commissions, fluidifier les deals, ni être perçu comme un professionnel crédible. Ce module vous donne toutes les clés pour négocier avec n'importe quelle contrepartie, dans n'importe quel pays."
      />

      <Section eyebrow="Fondation" title="La Méthode Harvard (Principled Negotiation)" index="1.1">
        <p>
          Développée par Roger Fisher et William Ury à l'Université Harvard, cette méthode est aujourd'hui utilisée
          dans les plus grandes négociations d'affaires et diplomatiques du monde. Elle repose sur <Bold>4 principes fondamentaux</Bold>.
        </p>

        <Sub title="PRINCIPE 1 — Séparer les personnes du problème">
          <p>
            Ne jamais attaquer la personne, attaquer le problème. Dans un deal international, les émotions peuvent détruire
            une négociation en quelques secondes. Un acheteur qui retarde le paiement n'est pas forcément de mauvaise foi —
            il peut avoir un problème de trésorerie passager, une pression hiérarchique, ou une contrainte bancaire. Traitez
            le problème (le délai de paiement), pas la personne. Cette distinction semble simple, mais elle demande une
            discipline réelle, surtout à distance ou par email où les malentendus sont fréquents.
          </p>
          <p>
            <Bold>Concrètement :</Bold> si une contrepartie vous envoie un email agressif ou irrationnel, ne répondez jamais
            à chaud. Attendez 2 heures minimum. Répondez au problème posé, pas au ton utilisé.
          </p>
        </Sub>

        <Sub title="PRINCIPE 2 — Se concentrer sur les intérêts, pas les positions">
          <p>
            La position, c'est ce que quelqu'un dit vouloir. L'intérêt, c'est <em>pourquoi</em> il le veut. Ces deux choses
            sont souvent très différentes.
          </p>
          <p>
            <Bold>Exemple :</Bold> un vendeur de pétrole dit « Je veux 85 $/baril » — c'est sa position. Mais son intérêt
            réel pourrait être « J'ai besoin d'un cash flow rapide ce mois-ci parce que j'ai des dettes à honorer ». En
            comprenant cet intérêt, vous pouvez proposer un paiement accéléré à 80 $/baril, ce qui satisfait tout le monde
            alors que la négociation position-contre-position serait restée bloquée.
          </p>
          <p>
            Dans les deals de matières premières, les intérêts cachés les plus fréquents sont : besoin de liquidité rapide,
            besoin de références (nouveaux fournisseurs qui cherchent leur premier gros client), besoin de discrétion,
            contraintes réglementaires dans leur pays, ou deadline de fin de trimestre comptable.
          </p>
        </Sub>

        <Sub title="PRINCIPE 3 — Inventer des options pour un bénéfice mutuel">
          <p>
            Avant d'entrer en négociation, brainstormez seul ou en équipe toutes les options possibles. Ne venez jamais
            avec une seule solution. Si le prix bloque, vous devez avoir 3 ou 4 alternatives prêtes :
          </p>
          <ul className="space-y-2 ml-4">
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Modifier l'incoterm (FOB au lieu de CIF, ce qui réduit le prix affiché)</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Proposer un paiement échelonné</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Offrir une garantie SBLC en lieu et place d'un LC à vue</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Proposer un volume engagé sur 12 mois en échange d'un meilleur prix</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Ajuster le calendrier de livraison</span></li>
          </ul>
          <p>
            L'objectif est de <Bold>créer de la valeur avant de la diviser</Bold>. Une négociation où les deux parties ont
            l'impression de gagner quelque chose est toujours plus solide qu'une négociation gagnant-perdant.
          </p>
        </Sub>

        <Sub title="PRINCIPE 4 — Insister sur des critères objectifs">
          <p>
            Basez tous vos arguments sur des données factuelles et vérifiables : prix spot LME du jour, indices Platts pour
            le pétrole, rapport SGS d'inspection, taux de fret maritime Clarksons, cours de change officiel BCE. Un critère
            objectif ne peut pas être contesté subjectivement.
          </p>
          <p>
            <Bold>Exemple :</Bold> « Le prix LME de l'aluminium aujourd'hui est 2 340 $/tonne. Ma proposition de 2 395 $ CIF
            inclut 35 $ de fret, 10 $ d'assurance, et 10 $ de ma commission. Vous pouvez vérifier le LME en temps réel. »
            Cette approche est infiniment plus efficace que « je pense que le prix est juste ».
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Application" title="Les 5 étapes d'une négociation réussie" index="1.2">
        <div className="space-y-5 mt-6">
          <NumberedItem n="01" title="Préparation (48 h avant)">
            <p>
              Avant toute négociation, définissez par écrit votre <Bold>BATNA</Bold> — <em>Best Alternative To a Negotiated
              Agreement</em>, c'est-à-dire votre meilleure alternative si la négociation échoue. Sans BATNA clair, vous ne
              savez pas quand partir, et vous risquez d'accepter un deal sous-optimal par peur du vide. Définissez également
              votre prix plancher absolu (en dessous duquel vous refusez), et votre objectif idéal (ce que vous espérez
              obtenir). La différence entre les deux est votre <Bold>zone de négociation</Bold>.
            </p>
          </NumberedItem>
          <NumberedItem n="02" title="Ouverture">
            <p>
              Laissez toujours l'autre partie parler en premier si possible. Celui qui donne le premier chiffre ancre
              psychologiquement la négociation. Si on vous demande d'ouvrir, donnez un chiffre ambitieux mais justifiable
              — jamais votre objectif réel. L'ouverture sert aussi à écouter : ce que l'autre dit en premier révèle souvent
              ses priorités et ses contraintes.
            </p>
          </NumberedItem>
          <NumberedItem n="03" title="Exploration">
            <p>
              Posez des questions ouvertes. « Qu'est-ce qui est le plus important pour vous dans ce deal ? » « Quels sont
              vos délais idéaux ? » « Y a-t-il des contraintes particulières côté financement ? » Plus vous en savez sur
              l'autre partie, plus vous pouvez adapter votre proposition à ses véritables besoins.
            </p>
          </NumberedItem>
          <NumberedItem n="04" title="Proposition">
            <p>
              Faites des propositions conditionnelles. La formule magique est : <Bold>« Si vous acceptez X, alors je peux
              faire Y. »</Bold> Exemple : « Si vous acceptez de passer de LC 90 jours à LC à vue, alors je peux vous offrir
              le prix CIF à X $ au lieu de Y $. » Ne faites jamais de concession unilatérale — chaque concession doit obtenir
              quelque chose en échange, même symbolique.
            </p>
          </NumberedItem>
          <NumberedItem n="05" title="Clôture">
            <p>
              Résumez l'accord par écrit immédiatement après l'accord verbal. Envoyez un email récapitulatif dans l'heure
              qui suit l'appel ou la réunion : « Suite à notre échange de ce jour, voici les termes convenus : [liste].
              Pourriez-vous confirmer votre accord ? » Un accord non confirmé par écrit dans les 24 h est souvent un accord
              perdu ou réinterprété.
            </p>
          </NumberedItem>
        </div>
      </Section>

      <Section eyebrow="Commissions" title="Négocier ses commissions sans se faire éjecter du deal" index="1.3">
        <p>
          C'est le point le plus délicat. Votre commission est légitime, mais la manière dont vous la présentez peut faire
          la différence entre un deal qui avance et un deal mort.
        </p>

        <Sub title="Les structures de commission courantes par secteur">
          <ul className="space-y-3">
            <li><Bold>Pétrole brut (gros volumes) :</Bold> 0,25 % à 1 % sur la valeur CIF du cargo. Sur un cargo standard
            de 2 millions de barils à 80 $/baril, cela représente 160 millions de dollars, soit une commission de 400 000 $
            à 1 600 000 $.</li>
            <li><Bold>Or physique :</Bold> 0,5 % à 2 % selon le volume et l'accès au produit. Pour 100 kg d'or à 75 000 $/kg,
            cela représente 75 000 $ à 150 000 $ de commission.</li>
            <li><Bold>Pierres précieuses :</Bold> 2 % à 5 % selon la rareté et la négociation. Marché de gré à gré, donc les
            marges sont plus souples.</li>
            <li><Bold>Ciment et matériaux industriels :</Bold> 1 % à 3 % sur la valeur FOB. Sur 50 000 tonnes à 60 $/tonne,
            cela représente 30 000 $ à 90 000 $.</li>
            <li><Bold>Horlogerie de luxe :</Bold> 3 % à 8 % selon le volume. Les marges sont plus élevées car le marché est
            moins transparent et l'accès aux fournisseurs est clé.</li>
            <li><Bold>Équipements industriels :</Bold> 2 % à 5 %.</li>
            <li><Bold>Produits agricoles (soft commodities) :</Bold> 0,5 % à 2 %.</li>
            <li><Bold>Services (conseil, financement, structuration) :</Bold> 5 % à 15 %.</li>
          </ul>
        </Sub>

        <Sub title="Les 3 niveaux de commission">
          <p>
            <Bold>NIVEAU 1 — Commission Open (visible) :</Bold> Toutes les parties savent que vous prenez une commission.
            Vous êtes clairement identifié comme broker. <em>Avantage</em> : transparence totale, moins de conflits.
            <em> Inconvénient</em> : vous pouvez être court-circuité plus facilement. Ce modèle fonctionne bien quand vous
            avez une relation établie et un NCNDA solide.
          </p>
          <p>
            <Bold>NIVEAU 2 — Commission Closed (confidentielle) :</Bold> Le vendeur vous verse votre commission séparément
            du prix affiché à l'acheteur. Vous apparaissez comme simple consultant ou facilitateur. C'est le modèle le plus
            fréquent dans les deals B2B internationaux. L'acheteur voit un prix CIF final, et ne sait pas ce que vous touchez.
            Votre commission est intégrée dans la marge du vendeur.
          </p>
          <p>
            <Bold>NIVEAU 3 — Back-to-back (double commission) :</Bold> Vous achetez auprès du vendeur à prix X et revendez
            à l'acheteur à prix X + votre marge. Vous êtes techniquement le principal dans la transaction. <em>Avantage</em> :
            vous contrôlez totalement le deal. <em>Inconvénient</em> : vous avez besoin d'une structure juridique solide,
            d'un compte bancaire dédié, et souvent d'un fonds de roulement ou d'une ligne de financement.
          </p>
        </Sub>

        <Sub title="Comment présenter sa commission sans friction">
          <p>
            <Bold>Ne jamais mentionner le pourcentage de commission en premier.</Bold> Établissez d'abord la valeur que vous
            apportez : « Je vous mets en relation avec un acheteur qualifié, solvable, avec LC confirmée par une banque de
            premier rang. Je gère la vérification des documents, la coordination logistique, et je reste disponible 7j/7
            jusqu'à réception des fonds. »
          </p>
          <Callout title="Phrase type pour normaliser la commission">
            <p className="italic text-[#F5F5DC]">
              « Ma rémunération est intégrée dans la structure du deal et ne change pas votre prix d'achat. »
            </p>
          </Callout>
          <p>
            Dans l'<Bold>IMFPA</Bold>, inscrivez votre commission comme un pourcentage du montant total de la transaction,
            payable directement par la banque du vendeur à votre banque, simultanément au paiement principal. Cela évite
            d'avoir à réclamer votre argent après coup.
          </p>
          <p>
            Protégez votre position avec un <Bold>NCNDA signé AVANT</Bold> de révéler vos contacts. Si une partie tente de
            vous éjecter, revenez à l'NCNDA et rappelez les pénalités contractuelles stipulées.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Tactique" title="Gérer les silences, les délais et les bluffs" index="1.4">
        <Sub title="Le silence comme outil de négociation">
          <p>
            Après avoir fait une proposition, <Bold>taisez-vous. Complètement.</Bold> Dans une négociation, celui qui parle
            en premier après une offre perd souvent. La plupart des gens sont inconfortables avec le silence et vont le
            combler — souvent en faisant une concession ou en révélant une information précieuse.
          </p>
          <p><Bold>Temps moyen de silence tolérable selon les cultures :</Bold></p>
          <ul className="space-y-2 ml-4">
            <li className="flex gap-3"><Clock className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span><Bold>Europe du Nord et USA :</Bold> 3 à 5 secondes maximum avant malaise</span></li>
            <li className="flex gap-3"><Clock className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span><Bold>Moyen-Orient :</Bold> 10 à 30 secondes, le silence signifie réflexion sérieuse</span></li>
            <li className="flex gap-3"><Clock className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span><Bold>Asie (Japon, Chine) :</Bold> parfois plusieurs minutes — ne l'interprétez jamais comme un refus</span></li>
            <li className="flex gap-3"><Clock className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span><Bold>Afrique francophone :</Bold> silence souvent comblé rapidement, peut signifier gêne ou désaccord indirect</span></li>
          </ul>
        </Sub>

        <Sub title="Gérer les délais tactiques">
          <p>
            Certaines parties allongent volontairement les délais pour tester votre patience ou votre niveau de besoin.
            <Bold> Plus vous semblez pressé, plus vous perdez du pouvoir.</Bold>
          </p>
          <p>
            <Bold>Stratégie 1 — Créez une deadline externe crédible :</Bold> « L'acheteur a une autre offre en cours de
            validation valable jusqu'au [date]. J'ai besoin de votre confirmation avant cette date pour maintenir sa
            priorité. »
          </p>
          <p>
            <Bold>Stratégie 2 — Utilisez la technique de l'escalation :</Bold> « Je dois présenter cette proposition à ma
            direction demain matin, j'aurais besoin de votre retour avant ce soir pour pouvoir la défendre. »
          </p>
          <p>
            <Bold>Stratégie 3 — Raccourcissez vos propres délais de réponse.</Bold> Si vous répondez toujours en moins
            d'une heure, vous créez une pression implicite de réciprocité. L'autre partie finit par accélérer aussi.
          </p>
        </Sub>

        <Sub title="Les bluffs courants et comment les contrer">
          <div className="space-y-4">
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <p className="font-serif text-white italic">« J'ai une meilleure offre ailleurs »</p>
              <p className="mt-2 text-sm">→ Répondre avec calme : « Parfait, si cette offre correspond mieux à vos besoins,
              nous vous souhaitons bonne continuation. » Dans 70 % des cas, ils rappellent dans les 48 heures. <Bold>Ne
              montrez jamais d'inquiétude.</Bold></p>
            </div>
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <p className="font-serif text-white italic">« Mon PDG doit approuver avant toute décision »</p>
              <p className="mt-2 text-sm">→ Proposer immédiatement un RDV en vidéoconférence avec le PDG. Si c'est impossible
              ou toujours repoussé, c'est un délai tactique pour gagner du temps.</p>
            </div>
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <p className="font-serif text-white italic">« Le prix du marché est plus bas que ce que vous proposez »</p>
              <p className="mt-2 text-sm">→ Demander la preuve : prix Bloomberg du jour, contrat concurrent visible,
              référence vérifiable. <Bold>S'ils ne peuvent pas produire ces preuves, c'est un bluff.</Bold></p>
            </div>
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <p className="font-serif text-white italic">« On peut faire ce deal sans intermédiaire »</p>
              <p className="mt-2 text-sm">→ Rappeler la valeur ajoutée concrète : vérification des contreparties, expertise
              documentaire, gestion des risques, coordination logistique. Et surtout rappeler l'NCNDA signé qui les expose
              à des pénalités.</p>
            </div>
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <p className="font-serif text-white italic">« C'est à prendre ou à laisser »</p>
              <p className="mt-2 text-sm">→ Répondre : « Je comprends votre position. Laissez-moi 24 heures pour consulter
              mon équipe. » <Bold>Ne jamais accepter une pression temporelle artificielle.</Bold> Cette formule est très
              rarement réelle.</p>
            </div>
          </div>
        </Sub>
      </Section>

      <Section eyebrow="Culture" title="Différences culturelles dans la négociation" index="1.5">
        <Sub title="Partenaires du Moyen-Orient (UAE, Arabie Saoudite, Qatar, Kuwait...)">
          <p>
            La <Bold>relation personnelle précède absolument et toujours le business</Bold>. Avant de parler chiffres,
            investissez du temps dans le small talk : famille, voyages, intérêts communs. Ne soyez jamais impatient de
            « rentrer dans le vif du sujet ».
          </p>
          <p>
            La notion de <Bold>« wajh »</Bold> (la face, l'honneur) est centrale dans toutes les cultures arabes. Ne mettez
            jamais quelqu'un en difficulté devant des tiers, ne le contredisez jamais publiquement, ne lui imposez jamais
            une situation d'échec visible.
          </p>
          <p>
            Le mot <Bold>« non »</Bold> est très rarement dit directement. « Peut-être », « Inch'Allah », « On va voir »,
            « C'est difficile » signifient souvent non. Apprenez à lire ces signaux indirects.
          </p>
          <p>
            Les décisions importantes ne se prennent presque jamais lors de la première réunion. La première réunion sert
            à évaluer l'humain, pas à conclure.
          </p>
          <p>
            Le <Bold>ramadan</Bold> ralentit fortement toutes les négociations. Les réunions sont moins fréquentes, les
            délais de réponse s'allongent. En revanche, les engagements pris pendant cette période ont souvent une valeur
            symbolique et morale forte.
          </p>
          <Callout title="Astuce pratique">
            Envoyez toujours vos documents en anglais ET en arabe si possible. Cela montre du respect et accélère la prise
            de décision interne.
          </Callout>
        </Sub>

        <Sub title="Partenaires Africains (Afrique subsaharienne et Maghreb)">
          <p>
            La communication est souvent <Bold>indirecte</Bold> — lisez entre les lignes. Un silence prolongé ou un
            changement de sujet peut signifier un désaccord non exprimé.
          </p>
          <p>
            Le principe d'<Bold>Ubuntu</Bold> (je suis parce que nous sommes) structure les relations : les intérêts
            collectifs et communautaires priment souvent sur l'individuel. Montrez que votre deal bénéficie à l'écosystème
            local, pas seulement à votre contrepartie.
          </p>
          <p>
            La confiance se construit par <Bold>présence physique ou appels vidéo réguliers</Bold>, pas seulement par
            emails. Un intermédiaire qui ne se montre jamais est difficile à croire.
          </p>
          <p>
            Les délais sont culturellement flexibles. Intégrez des marges de <Bold>50 %</Bold> dans tous vos plannings
            lorsque vous travaillez avec des contreparties africaines.
          </p>
          <p>
            Le fait d'être recommandé par quelqu'un de connu et respecté localement est souvent plus puissant que n'importe
            quelle accréditation ou présentation formelle.
          </p>
        </Sub>

        <Sub title="Partenaires Asiatiques (Chine, Japon, Corée du Sud)">
          <p>
            <Bold>Chine :</Bold> La négociation peut durer des mois. La première réunion ne sert qu'à se jauger mutuellement.
            Ne montrez jamais votre enthousiasme ou votre urgence. Les Chinois testent systématiquement la patience de leurs
            partenaires. Arrivez toujours avec une documentation écrite très détaillée.
          </p>
          <p>
            <Bold>Japon :</Bold> Le concept de <em>« nemawashi »</em> (littéralement « tourner autour des racines ») décrit
            le processus de consensus interne. La décision est prise collectivement en interne avant d'être annoncée. Cela
            prend du temps mais une fois annoncée, elle est définitive. Toujours présenter par écrit, en détail, avec des
            données.
          </p>
          <p>
            <Bold>Corée du Sud :</Bold> Les Coréens sont plus directs que les Japonais. Le respect de la hiérarchie est
            capital — identifiez et adressez-vous toujours au décideur réel, pas aux intermédiaires. La vitesse d'exécution
            une fois la décision prise est remarquable.
          </p>
          <p>
            <Bold>Dans toute l'Asie :</Bold> évitez de dire « non » directement. Proposez une alternative. Et la carte de
            visite doit être offerte et reçue à <Bold>deux mains</Bold>, avec un léger mouvement de tête. La jeter sur la
            table ou la plier est une insulte grave.
          </p>
        </Sub>

        <Sub title="Partenaires Européens">
          <p>
            <Bold>Allemagne :</Bold> Formalisme extrême, ponctualité absolue, exigence de précision technique dans tous les
            documents. Les Allemands veulent comprendre chaque détail avant de s'engager. Documentez tout.
          </p>
          <p>
            <Bold>France :</Bold> Le débat intellectuel est apprécié et même attendu. Les Français acceptent qu'on les
            contredise si l'argumentation est solide. Méfiance : un accord de principe verbal n'engage pratiquement pas en
            France. Tout doit être écrit.
          </p>
          <p>
            <Bold>Royaume-Uni :</Bold> L'understatement est une seconde langue. « C'est intéressant » peut vouloir dire
            qu'ils adorent. « Nous avons quelques réserves » peut vouloir dire non total. Apprenez à décoder.
          </p>
          <p>
            <Bold>Italie :</Bold> L'importance des relations personnelles et de l'esthétique. La forme de vos documents,
            de vos présentations, de vos emails compte presque autant que le fond.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Discipline" title="Savoir quand sortir d'un deal" index="1.6">
        <p>
          L'une des compétences les moins enseignées mais les plus précieuses : reconnaître un deal mort ou toxique et en
          sortir proprement avant de perdre du temps, de l'argent et de la réputation.
        </p>

        <Sub title="Les 7 signaux d'alerte qui indiquent qu'il faut partir">
          <div className="space-y-4">
            {[
              { n: 1, t: "Refus de signer", b: "La contrepartie refuse de signer le NCNDA ou l'IMFPA sans explication valable. Dans un deal légitime, ces documents sont standards. Un refus systématique révèle soit une intention de contournement, soit une totale méconnaissance du secteur." },
              { n: 2, t: "Documents qui changent", b: "Les documents changent à chaque échange. Prix modifiés, quantités révisées, termes de paiement qui évoluent constamment. Cela révèle soit un manque de sérieux, soit une tentative de vous embrouiller." },
              { n: 3, t: "Pas de preuves de propriété", b: "Aucun document de propriété ou de mandat officiel n'est fourni après 72 heures de demande. Un vendeur réel a ses documents prêts. Un vendeur qui n'a pas de preuves de disponibilité du produit après 3 jours ne les aura probablement jamais." },
              { n: 4, t: "Frais avancés non prévus", b: "On vous demande de payer des frais avancés non prévus dans les termes initiaux. C'est le signal d'alarme absolu. Voir module 3 pour les détails." },
              { n: 5, t: "Communications qui deviennent vagues", b: "Les communications deviennent vagues, les appels n'aboutissent plus, les réponses aux emails prennent des semaines. Un deal vivant a une dynamique active." },
              { n: 6, t: "Court-circuit en cours", b: "L'acheteur et le vendeur tentent de prendre contact directement en contournant votre rôle. Si cela se produit, revenez immédiatement à l'NCNDA et évaluez si le deal peut être sauvé." },
              { n: 7, t: "Votre instinct dit non", b: "Votre instinct dit que quelque chose ne va pas. Ne le rationalisez pas. Dans les affaires internationales, l'instinct entraîné d'un intermédiaire expérimenté a raison dans environ 80 % des cas. La plupart des gens qui se font arnaquer vous diront : « Je savais au fond que quelque chose clochait, mais j'ai continué. »" },
            ].map((s) => (
              <div key={s.n} className="flex gap-4 border border-[#FF3B30]/20 bg-[#FF3B30]/5 p-5">
                <AlertTriangle className="h-5 w-5 text-[#FF3B30] shrink-0 mt-0.5" />
                <div>
                  <div className="font-serif text-white">Signal {s.n} — {s.t}</div>
                  <p className="text-sm text-[#C4C4C7] mt-2 leading-relaxed">{s.b}</p>
                </div>
              </div>
            ))}
          </div>
        </Sub>

        <Sub title="Comment sortir élégamment">
          <Callout icon={DoorOpen} title="Modèle d'email de retrait">
            <p className="font-mono text-xs text-[#F5F5DC] whitespace-pre-wrap bg-black/40 p-4 border border-white/10">
{`Suite à nos échanges, nous constatons que les conditions
actuelles de ce deal ne nous permettent pas de répondre aux
exigences de nos parties respectives. Nous nous retirons
donc de cette procédure et souhaitons de bonnes suites à
votre projet.`}
            </p>
          </Callout>
          <p>
            <Bold>Ne jamais brûler les ponts.</Bold> Le partenaire d'aujourd'hui peut revenir avec un vrai deal dans 6 mois.
            Restez toujours professionnel, même si vous êtes certain d'avoir affaire à une fraude.
          </p>
          <p>
            Conservez absolument tous les échanges écrits dans un dossier archivé et daté. Ces documents peuvent servir en
            cas de litige ultérieur.
          </p>
        </Sub>
      </Section>
    </div>
  );
}

// =============================================================
// EN
// =============================================================
function NegotiationEN() {
  return (
    <div data-testid="view-negotiation" className="max-w-5xl">
      <Hero
        moduleNum="01"
        eyebrowLabel="Module"
        icon={Handshake}
        italic="International"
        accent="negotiation"
        description="Negotiation is the heart of the broker's craft. Without it, you can't secure your commissions, smooth out deals, or be perceived as a credible professional. This module gives you all the keys to negotiate with any counterparty, in any country."
      />

      <Section eyebrow="Foundation" title="The Harvard Method (Principled Negotiation)" index="1.1">
        <p>
          Developed by Roger Fisher and William Ury at Harvard University, this method is now used in the largest business
          and diplomatic negotiations in the world. It rests on <Bold>4 fundamental principles</Bold>.
        </p>

        <Sub title="PRINCIPLE 1 — Separate the people from the problem">
          <p>
            Never attack the person, attack the problem. In an international deal, emotions can destroy a negotiation in
            seconds. A buyer who delays payment is not necessarily acting in bad faith — they may have a temporary cash
            flow issue, hierarchical pressure, or a banking constraint. Address the problem (the payment delay), not the
            person. This distinction sounds simple, but it requires real discipline, especially over distance or email
            where misunderstandings are frequent.
          </p>
          <p>
            <Bold>In practice:</Bold> if a counterparty sends you an aggressive or irrational email, never reply hot. Wait
            at least 2 hours. Respond to the issue raised, not the tone used.
          </p>
        </Sub>

        <Sub title="PRINCIPLE 2 — Focus on interests, not positions">
          <p>
            A position is what someone says they want. An interest is <em>why</em> they want it. These two things are
            often very different.
          </p>
          <p>
            <Bold>Example:</Bold> an oil seller says "I want $85/barrel" — that's their position. But their real interest
            might be "I need fast cash flow this month because I have debts to honor." By understanding that interest, you
            can offer accelerated payment at $80/barrel, satisfying everyone — whereas position-versus-position would have
            stayed deadlocked.
          </p>
          <p>
            In commodity deals, the most common hidden interests are: need for fast liquidity, need for references (new
            suppliers looking for their first big client), need for discretion, regulatory constraints in their country,
            or end-of-quarter accounting deadlines.
          </p>
        </Sub>

        <Sub title="PRINCIPLE 3 — Invent options for mutual benefit">
          <p>
            Before entering a negotiation, brainstorm alone or with your team every possible option. Never come in with
            only one solution. If price stalls, you should have 3 or 4 alternatives ready:
          </p>
          <ul className="space-y-2 ml-4">
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Change the incoterm (FOB instead of CIF, lowering the headline price)</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Offer phased payments</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Offer an SBLC guarantee in place of a sight LC</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Commit to a 12-month volume in exchange for a better price</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Adjust the delivery schedule</span></li>
          </ul>
          <p>
            The goal is to <Bold>create value before splitting it</Bold>. A negotiation in which both parties feel they have
            won something is always more durable than a win-lose negotiation.
          </p>
        </Sub>

        <Sub title="PRINCIPLE 4 — Insist on objective criteria">
          <p>
            Base every argument on factual, verifiable data: today's LME spot price, Platts indices for oil, an SGS
            inspection report, Clarksons freight rates, the official ECB exchange rate. An objective criterion cannot be
            disputed subjectively.
          </p>
          <p>
            <Bold>Example:</Bold> "The LME aluminum price today is $2,340/ton. My CIF offer at $2,395 includes $35 of
            freight, $10 of insurance, and $10 of my commission. You can verify the LME in real time." This approach is
            infinitely more effective than "I think the price is fair."
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Application" title="The 5 stages of a successful negotiation" index="1.2">
        <div className="space-y-5 mt-6">
          <NumberedItem n="01" title="Preparation (48 h ahead)">
            <p>
              Before any negotiation, write down your <Bold>BATNA</Bold> — <em>Best Alternative To a Negotiated
              Agreement</em>, your best fallback if the negotiation fails. Without a clear BATNA, you don't know when to
              walk, and you risk accepting a sub-optimal deal out of fear of emptiness. Also define your absolute floor
              price (below which you refuse), and your ideal target (what you hope to obtain). The gap between the two is
              your <Bold>negotiation zone</Bold>.
            </p>
          </NumberedItem>
          <NumberedItem n="02" title="Opening">
            <p>
              Whenever possible, let the other side speak first. The one who names the first number psychologically anchors
              the negotiation. If you're asked to open, give an ambitious but justifiable figure — never your real target.
              Opening also serves to listen: what the other side says first often reveals their priorities and constraints.
            </p>
          </NumberedItem>
          <NumberedItem n="03" title="Exploration">
            <p>
              Ask open questions. "What's most important to you in this deal?" "What are your ideal timelines?" "Are there
              any specific constraints on the financing side?" The more you know about the other party, the more you can
              tailor your proposal to their real needs.
            </p>
          </NumberedItem>
          <NumberedItem n="04" title="Proposal">
            <p>
              Make conditional proposals. The magic formula is: <Bold>"If you accept X, then I can do Y."</Bold> Example:
              "If you switch from 90-day LC to sight LC, then I can offer the CIF price at $X instead of $Y." Never make a
              unilateral concession — every concession must obtain something in exchange, even symbolically.
            </p>
          </NumberedItem>
          <NumberedItem n="05" title="Closing">
            <p>
              Summarize the agreement in writing immediately after the verbal agreement. Send a recap email within an hour
              of the call or meeting: "Following our exchange today, here are the agreed terms: [list]. Could you confirm
              your agreement?" An agreement not confirmed in writing within 24 h is often a lost or reinterpreted agreement.
            </p>
          </NumberedItem>
        </div>
      </Section>

      <Section eyebrow="Commissions" title="Negotiating your commission without getting kicked out of the deal" index="1.3">
        <p>
          This is the most delicate point. Your commission is legitimate, but how you present it can make the difference
          between a deal that moves forward and a dead one.
        </p>

        <Sub title="Standard commission structures by sector">
          <ul className="space-y-3">
            <li><Bold>Crude oil (large volumes):</Bold> 0.25% to 1% of CIF cargo value. On a standard cargo of 2 million
            barrels at $80/barrel, that's $160 million, or a commission of $400,000 to $1,600,000.</li>
            <li><Bold>Physical gold:</Bold> 0.5% to 2% depending on volume and product access. For 100 kg of gold at
            $75,000/kg, that's $75,000 to $150,000 in commission.</li>
            <li><Bold>Gemstones:</Bold> 2% to 5% depending on rarity and negotiation. Over-the-counter market, so margins
            are more flexible.</li>
            <li><Bold>Cement and industrial materials:</Bold> 1% to 3% of FOB value. On 50,000 tons at $60/ton, that's
            $30,000 to $90,000.</li>
            <li><Bold>Luxury watches:</Bold> 3% to 8% depending on volume. Margins are higher because the market is less
            transparent and supplier access is key.</li>
            <li><Bold>Industrial equipment:</Bold> 2% to 5%.</li>
            <li><Bold>Soft commodities:</Bold> 0.5% to 2%.</li>
            <li><Bold>Services (advisory, financing, structuring):</Bold> 5% to 15%.</li>
          </ul>
        </Sub>

        <Sub title="The 3 commission tiers">
          <p>
            <Bold>TIER 1 — Open commission (visible):</Bold> All parties know you're taking a commission. You're clearly
            identified as a broker. <em>Upside</em>: total transparency, fewer conflicts. <em>Downside</em>: you can be
            bypassed more easily. This model works well when you have an established relationship and a solid NCNDA.
          </p>
          <p>
            <Bold>TIER 2 — Closed commission (confidential):</Bold> The seller pays your commission separately from the
            price shown to the buyer. You appear as a simple consultant or facilitator. This is the most frequent model
            in international B2B deals. The buyer sees a final CIF price and doesn't know what you receive. Your
            commission is built into the seller's margin.
          </p>
          <p>
            <Bold>TIER 3 — Back-to-back (double commission):</Bold> You buy from the seller at price X and resell to the
            buyer at price X + your margin. You're technically the principal in the transaction. <em>Upside</em>: you
            fully control the deal. <em>Downside</em>: you need a solid legal structure, a dedicated bank account, and
            often working capital or a financing line.
          </p>
        </Sub>

        <Sub title="How to present your commission without friction">
          <p>
            <Bold>Never mention the commission percentage first.</Bold> Establish your value first: "I'm connecting you
            with a qualified, solvent buyer with an LC confirmed by a top-tier bank. I handle document verification,
            logistics coordination, and remain available 7 days a week until funds are received."
          </p>
          <Callout title="Standard line to normalise the commission">
            <p className="italic text-[#F5F5DC]">
              "My fee is built into the deal structure and does not change your purchase price."
            </p>
          </Callout>
          <p>
            In the <Bold>IMFPA</Bold>, write your commission as a percentage of the total transaction amount, payable
            directly by the seller's bank to your bank, simultaneously with the principal payment. This avoids having to
            chase your money after the fact.
          </p>
          <p>
            Protect your position with a <Bold>signed NCNDA BEFORE</Bold> revealing your contacts. If a party tries to
            cut you out, return to the NCNDA and remind them of the contractual penalties.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Tactics" title="Handling silences, delays and bluffs" index="1.4">
        <Sub title="Silence as a negotiation tool">
          <p>
            After making an offer, <Bold>be silent. Completely.</Bold> In a negotiation, the one who speaks first after an
            offer usually loses. Most people are uncomfortable with silence and will fill it — often by making a concession
            or revealing valuable information.
          </p>
          <p><Bold>Average silence tolerance by culture:</Bold></p>
          <ul className="space-y-2 ml-4">
            <li className="flex gap-3"><Clock className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span><Bold>Northern Europe and the US:</Bold> 3 to 5 seconds maximum before discomfort</span></li>
            <li className="flex gap-3"><Clock className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span><Bold>Middle East:</Bold> 10 to 30 seconds, silence means serious reflection</span></li>
            <li className="flex gap-3"><Clock className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span><Bold>Asia (Japan, China):</Bold> sometimes several minutes — never read it as a refusal</span></li>
            <li className="flex gap-3"><Clock className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span><Bold>French-speaking Africa:</Bold> silence often filled quickly, can mean unease or indirect disagreement</span></li>
          </ul>
        </Sub>

        <Sub title="Handling tactical delays">
          <p>
            Some parties intentionally stretch timelines to test your patience or your level of need.
            <Bold> The more you appear rushed, the more power you lose.</Bold>
          </p>
          <p>
            <Bold>Strategy 1 — Create a credible external deadline:</Bold> "The buyer has another offer being validated,
            valid until [date]. I need your confirmation before that date to maintain their priority."
          </p>
          <p>
            <Bold>Strategy 2 — Use the escalation technique:</Bold> "I have to present this proposal to my management
            tomorrow morning, I'd need your reply tonight to defend it."
          </p>
          <p>
            <Bold>Strategy 3 — Shorten your own response times.</Bold> If you always reply in under an hour, you create
            implicit pressure of reciprocity. The other side ends up speeding up too.
          </p>
        </Sub>

        <Sub title="Common bluffs and how to counter them">
          <div className="space-y-4">
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <p className="font-serif text-white italic">"I have a better offer elsewhere"</p>
              <p className="mt-2 text-sm">→ Reply calmly: "Perfect — if that offer fits your needs better, we wish you all
              the best." In 70% of cases they call back within 48 hours. <Bold>Never show worry.</Bold></p>
            </div>
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <p className="font-serif text-white italic">"My CEO must approve before any decision"</p>
              <p className="mt-2 text-sm">→ Immediately propose a video call with the CEO. If it's impossible or always
              postponed, it's a tactical delay to buy time.</p>
            </div>
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <p className="font-serif text-white italic">"The market price is lower than what you're offering"</p>
              <p className="mt-2 text-sm">→ Ask for proof: today's Bloomberg price, a competing contract, a verifiable
              reference. <Bold>If they can't produce it, it's a bluff.</Bold></p>
            </div>
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <p className="font-serif text-white italic">"We can do this deal without an intermediary"</p>
              <p className="mt-2 text-sm">→ Recall the concrete value-add: counterparty verification, documentary
              expertise, risk management, logistics coordination. And especially recall the signed NCNDA which exposes
              them to penalties.</p>
            </div>
            <div className="border border-white/10 bg-[#0c0c0c] p-5">
              <p className="font-serif text-white italic">"Take it or leave it"</p>
              <p className="mt-2 text-sm">→ Reply: "I understand your position. Give me 24 hours to consult my team."
              <Bold>Never accept artificial time pressure.</Bold> This formula is very rarely real.</p>
            </div>
          </div>
        </Sub>
      </Section>

      <Section eyebrow="Culture" title="Cultural differences in negotiation" index="1.5">
        <Sub title="Middle East partners (UAE, Saudi Arabia, Qatar, Kuwait...)">
          <p>
            The <Bold>personal relationship absolutely and always precedes business</Bold>. Before discussing numbers,
            invest time in small talk: family, travel, common interests. Never appear impatient to "get to the point".
          </p>
          <p>
            The notion of <Bold>"wajh"</Bold> (face, honor) is central across all Arab cultures. Never put someone in
            difficulty in front of others, never publicly contradict them, never force a visibly losing situation on them.
          </p>
          <p>
            The word <Bold>"no"</Bold> is very rarely said directly. "Maybe", "Inshallah", "We'll see", "It's difficult"
            often mean no. Learn to read these indirect signals.
          </p>
          <p>
            Important decisions are almost never taken at the first meeting. The first meeting is for evaluating the
            human, not for closing.
          </p>
          <p>
            <Bold>Ramadan</Bold> strongly slows all negotiations. Meetings are less frequent, response times stretch.
            However, commitments made during this period often carry strong symbolic and moral value.
          </p>
          <Callout title="Practical tip">
            Always send your documents in English AND Arabic if possible. It shows respect and accelerates internal
            decision-making.
          </Callout>
        </Sub>

        <Sub title="African partners (Sub-Saharan Africa and Maghreb)">
          <p>
            Communication is often <Bold>indirect</Bold> — read between the lines. A prolonged silence or a topic shift
            may signal an unspoken disagreement.
          </p>
          <p>
            The <Bold>Ubuntu</Bold> principle (I am because we are) structures relationships: collective and community
            interests often prevail over the individual. Show how your deal benefits the local ecosystem, not just your
            counterparty.
          </p>
          <p>
            Trust is built through <Bold>physical presence or regular video calls</Bold>, not just emails. An intermediary
            who never shows up is hard to believe.
          </p>
          <p>
            Timelines are culturally flexible. Build <Bold>50%</Bold> margins into all your schedules when working with
            African counterparties.
          </p>
          <p>
            Being introduced by someone known and respected locally is often more powerful than any accreditation or
            formal pitch.
          </p>
        </Sub>

        <Sub title="Asian partners (China, Japan, South Korea)">
          <p>
            <Bold>China:</Bold> Negotiations can stretch over months. The first meeting only serves to size each other up.
            Never show enthusiasm or urgency. The Chinese systematically test their partners' patience. Always come with
            very detailed written documentation.
          </p>
          <p>
            <Bold>Japan:</Bold> The concept of <em>"nemawashi"</em> (literally "going around the roots") describes the
            internal consensus process. The decision is taken collectively before being announced. It takes time, but
            once announced it's final. Always present in writing, in detail, with data.
          </p>
          <p>
            <Bold>South Korea:</Bold> Koreans are more direct than the Japanese. Hierarchy respect is critical — identify
            and address the real decision-maker, not the intermediaries. Execution speed once the decision is taken is
            remarkable.
          </p>
          <p>
            <Bold>Across Asia:</Bold> avoid saying "no" directly. Offer an alternative. And the business card must be
            offered and received with <Bold>both hands</Bold>, with a slight bow. Tossing it on the table or folding it
            is a serious insult.
          </p>
        </Sub>

        <Sub title="European partners">
          <p>
            <Bold>Germany:</Bold> Extreme formalism, absolute punctuality, demand for technical precision in all
            documents. Germans want to understand every detail before committing. Document everything.
          </p>
          <p>
            <Bold>France:</Bold> Intellectual debate is appreciated and even expected. The French accept being
            contradicted if the argument is solid. Beware: a verbal agreement-in-principle commits practically nothing in
            France. Everything must be written.
          </p>
          <p>
            <Bold>UK:</Bold> Understatement is a second language. "That's interesting" might mean they love it. "We have
            some reservations" might mean a total no. Learn to decode.
          </p>
          <p>
            <Bold>Italy:</Bold> The importance of personal relationships and aesthetics. The form of your documents,
            presentations and emails matters almost as much as the substance.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Discipline" title="Knowing when to walk away" index="1.6">
        <p>
          One of the least-taught yet most valuable skills: recognising a dead or toxic deal and exiting cleanly before
          you lose time, money and reputation.
        </p>

        <Sub title="The 7 warning signals telling you to walk">
          <div className="space-y-4">
            {[
              { n: 1, t: "Refusal to sign", b: "The counterparty refuses to sign the NCNDA or IMFPA without a valid explanation. In a legitimate deal these documents are standard. A systematic refusal reveals either an intent to bypass, or a total ignorance of the sector." },
              { n: 2, t: "Documents that keep changing", b: "Documents change with every exchange. Prices revised, quantities edited, payment terms drifting constantly. This reveals either a lack of seriousness, or an attempt to confuse you." },
              { n: 3, t: "No proof of ownership", b: "No proof-of-ownership or official mandate is provided after 72 hours of asking. A real seller has their documents ready. A seller who can't show product availability after 3 days probably never will." },
              { n: 4, t: "Unexpected upfront fees", b: "You're asked to pay upfront fees that weren't in the initial terms. This is the absolute red flag. See module 3 for details." },
              { n: 5, t: "Communications going vague", b: "Communications become vague, calls don't connect, email replies take weeks. A live deal has active momentum." },
              { n: 6, t: "Bypass underway", b: "Buyer and seller try to reach each other directly, going around your role. If this happens, immediately return to the NCNDA and assess whether the deal can still be saved." },
              { n: 7, t: "Your gut says no", b: "Your gut says something's off. Don't rationalise it. In international business, the trained instinct of an experienced broker is right roughly 80% of the time. Most people who get scammed will tell you: \"I knew deep down something was off, but I kept going.\"" },
            ].map((s) => (
              <div key={s.n} className="flex gap-4 border border-[#FF3B30]/20 bg-[#FF3B30]/5 p-5">
                <AlertTriangle className="h-5 w-5 text-[#FF3B30] shrink-0 mt-0.5" />
                <div>
                  <div className="font-serif text-white">Signal {s.n} — {s.t}</div>
                  <p className="text-sm text-[#C4C4C7] mt-2 leading-relaxed">{s.b}</p>
                </div>
              </div>
            ))}
          </div>
        </Sub>

        <Sub title="How to exit gracefully">
          <Callout icon={DoorOpen} title="Withdrawal email template">
            <p className="font-mono text-xs text-[#F5F5DC] whitespace-pre-wrap bg-black/40 p-4 border border-white/10">
{`Following our exchanges, we note that the current terms of
this deal do not allow us to meet the requirements of our
respective parties. We are therefore withdrawing from this
process and wish your project every success going forward.`}
            </p>
          </Callout>
          <p>
            <Bold>Never burn the bridges.</Bold> Today's partner may come back with a real deal in 6 months. Always remain
            professional, even when you're certain you're dealing with fraud.
          </p>
          <p>
            Always keep all written exchanges in an archived, dated folder. These documents can be useful in case of
            later litigation.
          </p>
        </Sub>
      </Section>
    </div>
  );
}
